import type { PageIndex, Subpage } from '@textbox/core';

export interface PageEvent {
  page: number;
  sub: number;
  type: 'update' | 'remove';
  updatedAt: string;
}

/**
 * Where pages come from: static JSON (dev/tests) or the api (production),
 * both at `{base}/{ppp}/{sub}.json` and `{base}/index.json`. The api also
 * streams changes at `/events`.
 */
export interface PageSource {
  index(): Promise<PageIndex>;
  /** Resolves null when the page or subpage is not in service. */
  subpage(page: number, sub?: number): Promise<Subpage | null>;
  invalidate(page?: number): void;
  /** Live updates (server-sent events). Returns an unsubscribe function; a no-op when unsupported. */
  subscribe?(onEvent: (e: PageEvent) => void): () => void;
}

export class JsonPageSource implements PageSource {
  private cache = new Map<string, Promise<Subpage | null>>();
  private indexCache: Promise<PageIndex> | null = null;

  private base: string;
  private eventsUrl: string | null;
  private fetchImpl: typeof fetch;

  constructor(base = '/pages', eventsUrl: string | null = '/events', fetchImpl: typeof fetch = (...a) => fetch(...a)) {
    this.base = base;
    this.eventsUrl = eventsUrl;
    this.fetchImpl = fetchImpl;
  }

  subscribe(onEvent: (e: PageEvent) => void): () => void {
    if (!this.eventsUrl || typeof EventSource === 'undefined') return () => {};
    const es = new EventSource(this.eventsUrl);
    es.addEventListener('page', (ev) => {
      try {
        const e = JSON.parse((ev as MessageEvent).data) as PageEvent;
        this.invalidate(e.page);
        onEvent(e);
      } catch { /* ignore malformed events */ }
    });
    return () => es.close();
  }

  index(): Promise<PageIndex> {
    if (!this.indexCache) {
      this.indexCache = this.fetchImpl(`${this.base}/index.json`, { cache: 'no-cache' }).then(async (r) => {
        if (!r.ok) throw new Error(`index ${r.status}`);
        return (await r.json()) as PageIndex;
      });
      this.indexCache.catch(() => { this.indexCache = null; });
    }
    return this.indexCache;
  }

  subpage(page: number, sub = 1): Promise<Subpage | null> {
    const key = `${page}/${sub}`;
    let p = this.cache.get(key);
    if (!p) {
      p = this.fetchImpl(`${this.base}/${page}/${sub}.json`, { cache: 'no-cache' }).then(async (r) => {
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`page ${page}/${sub}: ${r.status}`);
        return (await r.json()) as Subpage;
      });
      this.cache.set(key, p);
      p.catch(() => this.cache.delete(key));
    }
    return p;
  }

  invalidate(page?: number): void {
    this.indexCache = null;
    if (page === undefined) this.cache.clear();
    else for (const k of [...this.cache.keys()]) if (k.startsWith(`${page}/`)) this.cache.delete(k);
  }
}
